import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useSettings } from '@wu/widget-core';
import { ViewType } from '../../types';
import { useVoteContext } from './vote-provider';

type AnimationContextProps = {
  areAllAnimationsCompleted: boolean;
  buttonAnimationEnd(id: string): void;
};

const AnimationContext = createContext<AnimationContextProps | undefined>(
  undefined
);

export function useAnimationContext(): AnimationContextProps {
  const context = useContext(AnimationContext);

  if (!context) {
    throw new Error(
      'useAnimationContext must be used within an AnimationProvider'
    );
  }

  return context;
}

export function AnimationProvider({ children }: PropsWithChildren) {
  const [areAllAnimationsCompleted, setAreAllAnimationsCompleted] =
    useState<boolean>(false);
  const [completedAnimations, setCompletedAnimations] = useState<string[]>([]);

  const { viewType } = useVoteContext();
  const { answers } = useSettings();

  const buttonAnimationEnd = (id: string) => {
    setCompletedAnimations((prev) => [...prev, id]);
  };

  useEffect(() => {
    if (completedAnimations.length === answers.length) {
      setCompletedAnimations([]);
      setAreAllAnimationsCompleted(true);
    }
  }, [completedAnimations.length, answers.length]);

  useEffect(() => {
    if (viewType === ViewType.VoteView) {
      setAreAllAnimationsCompleted(false);
    }
  }, [viewType]);

  return (
    <AnimationContext.Provider
      value={{ areAllAnimationsCompleted, buttonAnimationEnd }}
    >
      {children}
    </AnimationContext.Provider>
  );
}
